import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SubscriptionPlanSummary } from '@/types/subscription';

type UseSubscriptionPlansOptions = {
  includeFree?: boolean;
  enabled?: boolean;
  staleTimeMs?: number;
};

const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;

type PlansCacheEntry = {
  plans: SubscriptionPlanSummary[];
  fetchedAt: number;
};

type PlansCacheState = {
  plansCache: Map<string, PlansCacheEntry>;
  inFlightPlansRequests: Map<string, Promise<SubscriptionPlanSummary[]>>;
};

const SHARED_PLANS_CACHE_KEY = '__careeroffensive_subscription_plans_cache__';

const getSharedPlansCacheState = (): PlansCacheState => {
  const root = globalThis as typeof globalThis & {
    [SHARED_PLANS_CACHE_KEY]?: PlansCacheState;
  };

  if (!root[SHARED_PLANS_CACHE_KEY]) {
    root[SHARED_PLANS_CACHE_KEY] = {
      plansCache: new Map<string, PlansCacheEntry>(),
      inFlightPlansRequests: new Map<string, Promise<SubscriptionPlanSummary[]>>(),
    };
  }

  return root[SHARED_PLANS_CACHE_KEY]!;
};

const { plansCache, inFlightPlansRequests } = getSharedPlansCacheState();

const buildCacheKey = (includeFree: boolean) => `subscription_plans:${includeFree}`;

const getFreshCachedPlans = (cacheKey: string, staleTimeMs: number): SubscriptionPlanSummary[] | null => {
  const cached = plansCache.get(cacheKey);
  if (!cached) {
    return null;
  }
  if (Date.now() - cached.fetchedAt > staleTimeMs) {
    return null;
  }
  return cached.plans;
};

const fetchAndCachePlans = async (includeFree: boolean, cacheKey: string) => {
  const existingRequest = inFlightPlansRequests.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const response = await fetch(`/api/subscription/plans?includeFree=${includeFree}`, {
      cache: 'force-cache',
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'Failed to load plans');
    }

    const data = (await response.json()) as { plans?: SubscriptionPlanSummary[] };
    const plans = data.plans ?? [];
    plansCache.set(cacheKey, { plans, fetchedAt: Date.now() });
    return plans;
  })();

  inFlightPlansRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    inFlightPlansRequests.delete(cacheKey);
  }
};

export function useSubscriptionPlans(options: UseSubscriptionPlansOptions = {}) {
  const {
    includeFree = false,
    enabled = true,
    staleTimeMs = DEFAULT_STALE_TIME_MS,
  } = options;

  const cacheKey = useMemo(() => buildCacheKey(includeFree), [includeFree]);
  const [plans, setPlans] = useState<SubscriptionPlanSummary[]>(
    () => plansCache.get(cacheKey)?.plans ?? []
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    () => enabled && !getFreshCachedPlans(cacheKey, staleTimeMs)
  );
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async (config?: { force?: boolean }) => {
    if (!enabled && !config?.force) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      if (!config?.force) {
        const cachedPlans = getFreshCachedPlans(cacheKey, staleTimeMs);
        if (cachedPlans) {
          setPlans(cachedPlans);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);
      const fetchedPlans = await fetchAndCachePlans(includeFree, cacheKey);
      setPlans(fetchedPlans);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
      setPlans((currentPlans) => {
        if (currentPlans.length > 0) {
          return currentPlans;
        }
        return plansCache.get(cacheKey)?.plans ?? [];
      });
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey, enabled, includeFree, staleTimeMs]);

  useEffect(() => {
    const cachedPlans = plansCache.get(cacheKey)?.plans ?? [];
    setPlans(cachedPlans);
    setError(null);

    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(!getFreshCachedPlans(cacheKey, staleTimeMs));
  }, [cacheKey, enabled, staleTimeMs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void fetchPlans();
  }, [enabled, fetchPlans]);

  const refetch = useCallback(() => fetchPlans({ force: true }), [fetchPlans]);

  return { plans, isLoading, error, refetch };
}
